import React, { useState } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import { Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * One-time prompt shown to Google sign-ups who don't have a phone number yet.
 * Also captures their email preferences. Rendered globally when the
 * authenticated user has `needs_phone_prompt`.
 */
export const PhonePromptModal = () => {
  const { user, checkAuth } = useAuth();
  const [phone, setPhone] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [inventoryOptIn, setInventoryOptIn] = useState(true);
  const [saving, setSaving] = useState(false);

  if (!user?.needs_phone_prompt) return null;

  const authHeaders = () => {
    const t = localStorage.getItem('authToken');
    return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  const save = async () => {
    if (phone.replace(/\D/g, '').length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/me/phone`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ phone, newsletter_opt_in: newsletterOptIn, inventory_opt_in: inventoryOptIn }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Could not save phone number');
      }
      toast.success('Thanks! Your details have been saved.');
      await checkAuth();
    } catch (err) {
      toast.error(err.message || 'Could not save phone number');
    } finally {
      setSaving(false);
    }
  };

  const skip = async () => {
    setSaving(true);
    try {
      await fetch(`${API_URL}/api/auth/me/phone/dismiss`, { method: 'POST', headers: authHeaders() });
      await checkAuth();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4" data-testid="phone-prompt-modal">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6">
        <div className="flex items-center gap-3 mb-3">
          <div className="h-11 w-11 rounded-full bg-teal-50 flex items-center justify-center">
            <Phone className="h-5 w-5 text-teal-700" />
          </div>
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Finish setting up your account</h3>
            <p className="text-sm text-slate-500">Add your phone number and choose what you'd like to hear about.</p>
          </div>
        </div>
        <div className="space-y-2 mt-4">
          <Label htmlFor="prompt-phone">Phone Number</Label>
          <div className="relative">
            <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              id="prompt-phone"
              type="tel"
              placeholder="(555) 123-4567"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="pl-10"
              data-testid="phone-prompt-input"
            />
          </div>
        </div>

        <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50 p-3 mt-4">
          <p className="text-sm font-medium text-slate-700">Stay in the loop (optional)</p>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={inventoryOptIn}
              onCheckedChange={(v) => setInventoryOptIn(!!v)}
              className="mt-0.5"
              data-testid="phone-prompt-inventory-optin"
            />
            <span className="text-sm text-slate-600 leading-snug">
              <span className="font-medium text-slate-700">Inventory &amp; restock alerts</span> — email me when products are back in stock or new items arrive.
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox
              checked={newsletterOptIn}
              onCheckedChange={(v) => setNewsletterOptIn(!!v)}
              className="mt-0.5"
              data-testid="phone-prompt-newsletter-optin"
            />
            <span className="text-sm text-slate-600 leading-snug">
              <span className="font-medium text-slate-700">Newsletter &amp; research updates</span> — occasional articles, guides and member offers.
            </span>
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-6">
          <Button variant="ghost" onClick={skip} disabled={saving} data-testid="phone-prompt-skip">
            Not now
          </Button>
          <Button className="bg-teal-700 hover:bg-teal-800 text-white" onClick={save} disabled={saving} data-testid="phone-prompt-save">
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PhonePromptModal;
